import {Controller} from "@hotwired/stimulus"

// Connects to data-controller="event-date-time"
export default class extends Controller {
  static targets = ["dateTimeText", "dateTimeInput", "eventType", 'timezoneWrapper',
    'timezoneInput', 'dateTimeTextWrapper']

  static values = {
    editForm: Boolean,
    startDate: String,
    endDate: String,
    startTime: String,
    endTime: String,
    eventType: String,
    timeZone: String,
    dateTimePlaceholder: String,
  }

  connect() {
    if (this.editFormValue) this.hideDateTimeFields()
    this.mnualTimeUpdate = false
  }

  updateDateTimeText() {
    const eventType = this.eventTypeValue; // single, recurring, or multiple-day
    const startDate = this.hasStartDateValue && this.startDateValue != '' ? this.formatDate(this.startDateValue) : null
    let startTime = ''
    let endTime = ''
    const endDate = this.hasEndDateValue && this.endDateValue != '' ? this.formatDate(this.endDateValue) : null
    if (this.mnualTimeUpdate){
      startTime = this.startTimeValue != '' ? this.startTimeValue : null
      endTime = this.endTimeValue != '' ? this.endTimeValue : null
    }else {
      startTime = this.hasStartTimeValue && this.startTimeValue != '' ? this.formatTime(this.startTimeValue) : null
      endTime = this.hasEndTimeValue && this.endTimeValue != '' ? this.formatTime(this.endTimeValue) : null
    }

    // When the form has enough input, emit the same three-span structure the
    // server helper (event_date_time_builder) renders — .dt-date / .dt-time /
    // .dt-tz — so the CSS stacks date, time and timezone on separate lines and
    // the text stays clear of the absolutely-positioned Edit pill. Until then,
    // fall back to the flat placeholder text.
    if (startDate && startTime && endTime &&
        (eventType === "single_event" || (startDate && endDate))) {
      const dateStr = eventType === "single_event"
        ? startDate
        : `${startDate} to ${endDate}`;
      const timeStr = `${startTime} - ${endTime}`;
      this.renderDateTimeSpans(dateStr, timeStr, this.timeZoneAbbr());
    } else {
      this.dateTimeTextTarget.textContent = this.dateTimePlaceholderValue;
    }
  }

  // Render the date/time/timezone into three spans, mirroring the server-side
  // event_date_time_spans helper. Uses textContent on created elements so the
  // values are never interpreted as HTML.
  renderDateTimeSpans(dateStr, timeStr, tz) {
    this.dateTimeTextTarget.replaceChildren(
      this.dateTimeSpan("dt-date", dateStr),
      this.dateTimeSpan("dt-time", timeStr),
      this.dateTimeSpan("dt-tz", tz)
    );
  }

  dateTimeSpan(className, text) {
    const span = document.createElement("span");
    span.className = className;
    span.textContent = text || "";
    return span;
  }

  // Parse the date WITHOUT timezone interpretation. `new Date("2026-05-28")`
  // treats the string as UTC midnight, which shows as the previous day in any
  // zone behind UTC. We instead pull the y/m/d parts out of the string (accepts
  // "YYYY-MM-DD" from the server value and "MM-DD-YYYY" from the flatpickr
  // input) and build a local date so the day is exactly what was entered.
  formatDate(dateObj) {
    const str = String(dateObj).trim();
    let y, m, d;
    let match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);        // YYYY-MM-DD
    if (match) {
      [, y, m, d] = match;
    } else {
      match = str.match(/^(\d{1,2})-(\d{1,2})-(\d{4})/);      // MM-DD-YYYY
      if (match) { [, m, d, y] = match; }
    }
    let date;
    if (y) {
      date = new Date(Number(y), Number(m) - 1, Number(d));   // local-time date
    } else {
      date = new Date(str);                                   // fallback
    }
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'long' });
    const year = date.getFullYear();
    return `${month} ${day}, ${year}`;
  }

  // Short timezone abbreviation (e.g. "PDT") for the event's timezone, instead
  // of the raw IANA id ("America/Los_Angeles"). DST-correct for the start date
  // when available. Falls back to the raw value if Intl can't resolve it.
  timeZoneAbbr() {
    const tz = this.timeZoneValue;
    if (!tz) return "";
    try {
      const anchor = this.startDateForAbbr();
      const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" })
        .formatToParts(anchor);
      const name = parts.find(p => p.type === "timeZoneName");
      return name ? name.value : tz;
    } catch (e) {
      return tz;
    }
  }

  startDateForAbbr() {
    const str = this.hasStartDateValue ? String(this.startDateValue).trim() : "";
    let match = str.match(/^(\d{4})-(\d{2})-(\d{2})/) || str.match(/^(\d{1,2})-(\d{1,2})-(\d{4})/);
    if (!match) return new Date();
    // Build a noon date to avoid edge DST flips at midnight.
    if (str.indexOf("-") === 4) {  // YYYY-MM-DD
      return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12);
    }
    return new Date(Number(match[3]), Number(match[1]) - 1, Number(match[2]), 12);  // MM-DD-YYYY
  }


  formatTime(time) {
    // Parse time without timezone conversion
    // Extract hours and minutes directly from the time string to avoid browser timezone issues
    const timeString = time.toString();

    // Handle format like "2000-01-01 13:00:00 UTC" or "13:00:00"
    const timeMatch = timeString.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);

    if (timeMatch) {
      let hours = parseInt(timeMatch[1]);
      const minutes = timeMatch[2];
      const period = hours >= 12 ? 'PM' : 'AM';

      // Convert to 12-hour format
      hours = hours % 12 || 12;

      return `${hours}:${minutes} ${period}`;
    }

    // Fallback if parsing fails
    return time;
  }

  hideDateTimeFields() {
    this.updateDateTimeText()
    if (this.hasDateTimeTextWrapperTarget) this.dateTimeTextWrapperTarget.classList.remove("d-none");
    if (this.hasDateTimeInputTarget) this.dateTimeInputTarget.classList.add("d-none");
    if (this.hasTimezoneWrapperTarget) this.timezoneWrapperTarget.classList.add('d-none')
  }

  updateDate() {
    if(event.currentTarget.dataset.startDate == 'true') {
      this.startDateValue = this.formatDate(event.currentTarget.value)
    } else {
      this.endDateValue = this.formatDate(event.currentTarget.value)
    }

    this.eventTypeValue = this.resetTabsFieldsController.eventTypeInputTarget.value
  }

  updateTime() {
    this.mnualTimeUpdate = true
    if(event.currentTarget.dataset.startTime == 'true') {
      this.startTimeValue = event.currentTarget.value
    } else {
      this.endTimeValue = event.currentTarget.value
    }
  }

  get resetTabsFieldsController() {
    return this.application.controllers.find(controller => {
      return controller.context.identifier === 'reset-tabs-fields';
    });
  }
}
